import { AppRoutes } from "@/routes/config";
import { logout } from "@/utils/logout";
import { CENTER_URL, initiateOAuthLogin } from "@/utils/oauth";
import { rememberOAuthIdentity, TOAuthLoginReason } from "@/utils/oauthTelemetry";
import { createUrlWithParams } from "@/utils/url";

type TRedirectToLoginOptions = {
  /**
   * Clear the auth token (and related cookies) before redirecting.
   */
  clearToken?: boolean;
  /**
   * Why the session is being handed back. Recorded by the OAuth loop breaker so
   * a match <-> center ping-pong is reported with the branch that caused it.
   */
  reason?: TOAuthLoginReason;
};

/**
 * Sends the user to the appropriate login entry point, preserving the current
 * page as the return URL so they land back where they were after signing in:
 * - Center configured → hand off to the center OAuth login directly.
 * - Otherwise → the local login page.
 *
 * The return URL is captured BEFORE clearing cookies, and the local redirect is
 * skipped when already on the login page so a 401 storm cannot loop.
 */
export const redirectToLogin = (options?: TRedirectToLoginOptions) => {
  const oldUrl = `${location.pathname}${location.search}`;

  // Read the identity from the access token claims before it is cleared below,
  // so a redirect loop is still reported with the account it happened to.
  rememberOAuthIdentity();

  // Clear the token before leaving so the login page starts from a clean session.
  if (options?.clearToken) {
    logout();
  }

  // Center owns authentication when configured — skip the local login hop.
  // `initiateOAuthLogin` resolves its own return URL (and handles being called
  // from `/login`, where the target already sits in the query string).
  if (CENTER_URL) {
    initiateOAuthLogin(options?.reason);
    return;
  }

  if (location.pathname.startsWith(AppRoutes.login)) return;

  location.href = createUrlWithParams(AppRoutes.login, { redirectUrl: oldUrl });
};
